/**
 * Menu List Page - Admin Menu Management
 * 
 * Displays all menu items with search/filter functionality.
 * Fetches data from /api/menu endpoint.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HiPlus, 
  HiSearch, 
  HiPencil, 
  HiTrash, 
  HiEye, 
  HiEyeOff,
  HiRefresh,
  HiClock,
  HiShoppingCart,
  HiFire,
  HiStar,
  HiDuplicate
} from 'react-icons/hi';
import Tooltip from '../../components/Tooltip';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// Menu item type
interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  image_url: string | null;
  is_available: boolean;
  is_featured: boolean;
  is_orderable_online: boolean;
  is_popular: boolean;
  /** True when Tammy marks this item as baking today — shows a green badge on the site */
  is_baking_today: boolean;
  created_at: string;
  display_order?: number;
  requires_approval?: boolean;
  approval_form_type?: 'cake' | 'tray' | 'generic';
}

export default function MenuList() {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [bulkPercent, setBulkPercent] = useState('');
  const [bulkCategory, setBulkCategory] = useState<string>('all');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderCategory, setReorderCategory] = useState<string>('');
  const [reorderIds, setReorderIds] = useState<string[]>([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  // Fetch menu items from API
  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch('/api/menu');
      if (!response.ok) throw new Error('Failed to fetch menu items');
      const data = await response.json();
      setItems(data.items || []);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(data.items.map((item: MenuItem) => item.category))];
      setCategories(uniqueCategories.filter(Boolean) as string[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  // Load items on mount
  useEffect(() => {
    fetchItems();
  }, []);

  // Toggle item availability
  const toggleAvailability = async (id: string) => {
    try {
      const response = await adminFetch(`/api/menu/${id}/toggle-available`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      
      // Update local state
      setItems(items.map(item => 
        item.id === id ? { ...item, is_available: !item.is_available } : item
      ));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update availability';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Toggle "baking today" flag — marks which items Tammy is making today
  const toggleBakingToday = async (id: string) => {
    try {
      const response = await adminFetch(`/api/menu/${id}/toggle-baking-today`, { method: 'PUT' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      setItems(items.map(item =>
        item.id === id ? { ...item, is_baking_today: !item.is_baking_today } : item
      ));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update';
      alert(`❌ ${message}`);
    }
  };

  // Reset all "baking today" flags — use at the end of the day or next morning
  const resetBakingToday = async () => {
    const cronKey = prompt('Enter the CRON_SECRET key to reset all Baking Today flags:');
    if (!cronKey) return;
    try {
      const res = await fetch(`/api/cron/reset-baking-today?key=${encodeURIComponent(cronKey)}`);
      if (!res.ok) throw new Error('Unauthorized or server error');
      setItems(items.map(item => ({ ...item, is_baking_today: false })));
      alert('✅ All "Baking Today" flags have been cleared.');
    } catch (err) {
      alert('❌ Reset failed. Check your CRON_SECRET key and try again.');
    }
  };

  // Duplicate menu item
  const duplicateItem = async (id: string) => {
    try {
      const response = await adminFetch(`/api/menu/${id}/duplicate`, { method: 'POST' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to duplicate');
      }
      const created = await response.json();
      setItems(prev => [created, ...prev]);
      window.location.href = `/admin/menu/${created.id}`;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to duplicate item';
      alert(`❌ ${message}`);
    }
  };

  const applyBulkPrice = async () => {
    const pct = parseFloat(bulkPercent);
    if (Number.isNaN(pct) || pct === 0) {
      alert('Enter a non-zero percent (e.g. 5 for +5%, -10 for -10%).');
      return;
    }
    setBulkApplying(true);
    try {
      const res = await adminFetch('/api/menu/bulk-price', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          category: bulkCategory === 'all' ? undefined : bulkCategory,
          percent_change: pct,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to update prices');
      }
      const data = await res.json();
      alert(`Updated ${data.updated} item(s).`);
      setBulkPercent('');
      fetchItems();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update prices');
    } finally {
      setBulkApplying(false);
    }
  };

  const openReorderModal = () => {
    const cat = categories[0] || '';
    setReorderCategory(cat);
    const inCategory = items
      .filter((i) => i.category === cat)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    setReorderIds(inCategory.map((i) => i.id));
    setReorderModalOpen(true);
  };

  const reorderMove = (index: number, dir: number) => {
    const next = index + dir;
    if (next < 0 || next >= reorderIds.length) return;
    const arr = [...reorderIds];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setReorderIds(arr);
  };

  const saveReorder = async () => {
    setReorderSaving(true);
    try {
      const res = await adminFetch('/api/menu/reorder', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ item_ids: reorderIds }),
      });
      if (!res.ok) throw new Error('Failed to save order');
      setReorderModalOpen(false);
      fetchItems();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save order');
    } finally {
      setReorderSaving(false);
    }
  };

  // Toggle requires approval
  const toggleApproval = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    try {
      const response = await adminFetch(`/api/menu/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          requires_approval: !item.requires_approval,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      
      // Update local state
      setItems(items.map(i => 
        i.id === id ? { ...i, requires_approval: !i.requires_approval } : i
      ));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update approval setting';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete item — API returns clear steps when a fundraiser campaign still uses the item.
  const deleteItem = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      const response = await adminFetch(`/api/menu/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const apiMessage =
          typeof errorData.error === 'string' && errorData.error.trim()
            ? errorData.error.trim()
            : 'Failed to delete';
        // Campaign / FK blockers already include full instructions — show as-is.
        if (errorData.code === 'MENU_ITEM_USED_BY_CAMPAIGN' || response.status === 409) {
          alert(apiMessage);
          return;
        }
        throw new Error(apiMessage);
      }
      
      // Remove from local state
      setItems(items.filter(item => item.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete item';
      alert(
        `Could not delete "${name}".\n\n${message}\n\n` +
          `If you only need it off the store for now, click Hide instead.\n` +
          `If it keeps failing, tell Humberto which item you tried to delete.`
      );
    }
  };

  // Filter items based on search and category
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Format price
  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Menu Items</h1>
          <p className="text-gray-500 mt-1">
            {items.length} item{items.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchItems}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 
                       rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {/* Reset "Baking Today" — clears all green badges. Use at end of day. */}
          <Tooltip content="Clears all 'Baking Today' green badges. Do this at end of day or next morning.">
            <button
              onClick={resetBakingToday}
              className="inline-flex items-center gap-2 px-3 py-2 border border-green-300 
                         text-green-700 rounded-lg hover:bg-green-50 transition-colors text-sm"
            >
              <HiFire className="w-4 h-4" />
              Reset Baking Today
            </button>
          </Tooltip>
          <Tooltip content="Create a new product for your menu. Add name, price, category, and upload a photo. You can add it to collections later!">
            <Link
              to="/admin/menu/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 
                         text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <HiPlus className="w-5 h-5" />
              Add Item
            </Link>
          </Tooltip>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl
                       focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-xl bg-white
                     focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          aria-label="Filter by category"
          title="Filter by category"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Bulk actions */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Bulk actions</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Bulk price change</label>
            <div className="flex gap-2">
              <select
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
              >
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="number"
                value={bulkPercent}
                onChange={(e) => setBulkPercent(e.target.value)}
                placeholder="e.g. 5 or -10"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <span className="self-center text-sm text-gray-500">%</span>
              <button
                type="button"
                onClick={applyBulkPrice}
                disabled={bulkApplying}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                {bulkApplying ? 'Applying...' : 'Apply'}
              </button>
            </div>
          </div>
          <div>
            <button
              type="button"
              onClick={openReorderModal}
              disabled={categories.length === 0}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reorder by category
            </button>
          </div>
        </div>
      </div>

      {/* Reorder modal */}
      {reorderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 max-h-[80vh] overflow-hidden flex flex-col">
            <h3 className="font-semibold text-gray-900 mb-3">Reorder items</h3>
            <select
              value={reorderCategory}
              onChange={(e) => {
                const cat = e.target.value;
                setReorderCategory(cat);
                const inCategory = items
                  .filter((i) => i.category === cat)
                  .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
                setReorderIds(inCategory.map((i) => i.id));
              }}
              className="mb-3 px-3 py-2 border border-gray-300 rounded-lg w-full"
            >
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <ul className="flex-1 overflow-y-auto space-y-1 mb-4">
              {reorderIds.map((id, idx) => {
                const item = items.find((i) => i.id === id);
                return (
                  <li key={id} className="flex items-center gap-2 py-2 border-b border-gray-100">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => reorderMove(idx, -1)}
                        disabled={idx === 0}
                        className="text-gray-400 hover:text-purple-600 disabled:opacity-30"
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => reorderMove(idx, 1)}
                        disabled={idx === reorderIds.length - 1}
                        className="text-gray-400 hover:text-purple-600 disabled:opacity-30"
                        aria-label="Move down"
                      >
                        ▼
                      </button>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{item?.name ?? id}</span>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReorderModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveReorder}
                disabled={reorderSaving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {reorderSaving ? 'Saving...' : 'Save order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
          <button onClick={fetchItems} className="ml-2 underline">Try again</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading menu items...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredItems.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">🧁</div>
          <h3 className="text-lg font-semibold text-gray-900">
            {items.length === 0 ? 'No menu items yet' : 'No items match your search'}
          </h3>
          <p className="text-gray-500 mt-2">
            {items.length === 0 
              ? 'Add your first menu item to get started.' 
              : 'Try a different search term or category.'}
          </p>
          {items.length === 0 && (
            <Link
              to="/admin/menu/new"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 
                         text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <HiPlus className="w-5 h-5" />
              Add First Item
            </Link>
          )}
        </div>
      )}

      {/* Menu Items Grid */}
      {!loading && filteredItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map(item => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border overflow-hidden transition-all
                          ${item.is_available 
                            ? 'border-gray-200 hover:shadow-md' 
                            : 'border-gray-200 opacity-60'}`}
            >
              {/* Image */}
              <div className="aspect-video bg-gray-100 relative">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl">
                    🧁
                  </div>
                )}
                {/* Badge row - top left */}
                <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                  {item.is_featured && (
                    <span className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded flex items-center gap-1">
                      <HiStar className="w-3 h-3" />
                      Featured
                    </span>
                  )}
                  {item.is_popular && (
                    <span className="px-2 py-1 bg-pink-500 text-white text-xs font-medium rounded flex items-center gap-1">
                      <HiFire className="w-3 h-3" />
                      Popular
                    </span>
                  )}
                  {item.is_orderable_online && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded flex items-center gap-1">
                      <HiShoppingCart className="w-3 h-3" />
                      Order
                    </span>
                  )}
                </div>
                {/* Requires Approval badge - top right */}
                {item.requires_approval && (
                  <span className="absolute top-2 right-2 px-2 py-1 bg-purple-600 text-white text-xs font-medium rounded flex items-center gap-1">
                    <HiClock className="w-3 h-3" />
                    Approval
                  </span>
                )}
                {/* Unavailable overlay */}
                {!item.is_available && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="px-3 py-1 bg-red-500 text-white text-sm font-medium rounded">
                      Unavailable
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                      {item.category}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-purple-600">
                    {formatPrice(item.price)}
                  </span>
                </div>
                {item.description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                    {item.description}
                  </p>
                )}

                {/* Baking Today Toggle — Tammy marks this each morning */}
                <div className="flex items-center justify-between py-2 mb-1 border-b border-gray-100">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <HiFire className="w-4 h-4 text-orange-500" />
                    Baking Today
                  </span>
                  <button
                    onClick={() => toggleBakingToday(item.id)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                               ${item.is_baking_today
                                 ? 'bg-green-500'
                                 : 'bg-gray-200'}`}
                    title={item.is_baking_today ? 'Remove from baking today' : 'Mark as baking today'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                 ${item.is_baking_today ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>

                {/* Approval Toggle */}
                <div className="flex items-center justify-between py-2 mb-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <HiClock className="w-4 h-4" />
                    Requires Approval
                  </span>
                  <button
                    onClick={() => toggleApproval(item.id)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                               ${item.requires_approval 
                                 ? 'bg-purple-600' 
                                 : 'bg-gray-200'}`}
                    title={item.requires_approval ? 'Turn off approval' : 'Turn on approval'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                 ${item.requires_approval ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleAvailability(item.id)}
                    className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 
                               text-sm rounded-lg transition-colors
                               ${item.is_available 
                                 ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                                 : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                    title={item.is_available ? 'Mark unavailable' : 'Mark available'}
                  >
                    {item.is_available ? <HiEyeOff className="w-4 h-4" /> : <HiEye className="w-4 h-4" />}
                    {item.is_available ? 'Hide' : 'Show'}
                  </button>
                  <Link
                    to={`/admin/menu/${item.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 
                               text-sm bg-purple-100 text-purple-700 rounded-lg 
                               hover:bg-purple-200 transition-colors"
                  >
                    <HiPencil className="w-4 h-4" />
                    Edit
                  </Link>
                  <Tooltip position="bottom" content="Duplicate this item">
                    <button
                      type="button"
                      onClick={() => duplicateItem(item.id)}
                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Duplicate"
                    >
                      <HiDuplicate className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <button
                    onClick={() => deleteItem(item.id, item.name)}
                    className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg 
                               hover:bg-red-200 transition-colors"
                    title="Delete"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
